"use client";

/**
 * /android — the Android app's home (Gordon 2026-11-01: "We need 2 more pages
 * … /ios … /android"). Android already installs BB as a real app from Chrome
 * (the PWA: own icon, own window, offline shell) — the Install button uses the
 * browser's own install prompt where it offers one. The Google Play version
 * (a Trusted Web Activity wrapping the same app) comes after the founders'
 * test; set PLAY_STORE_URL when the listing is live.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Check, Download, EllipsisVertical, FlaskConical, Smartphone, Usb } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Set when the Google Play listing is live. */
const PLAY_STORE_URL = "";

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

function PlayIcon({ className }: { className?: string }) {
  // Generic "play store" triangle — not Google's trademarked logo.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
      <path d="M5 3.5v17a1 1 0 0 0 1.5.86l14-8.5a1 1 0 0 0 0-1.72l-14-8.5A1 1 0 0 0 5 3.5z" />
    </svg>
  );
}

export default function AndroidPage() {
  const t = useTranslations("androidApp");
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    } catch { /* old browser */ }
    const onPrompt = (e: Event) => { e.preventDefault(); setPrompt(e as InstallPrompt); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  return (
    <div className="w-full px-page py-8">
      <div className="max-w-3xl space-y-10">
        <header className="space-y-3">
          <p className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary-foreground">
            <PlayIcon className="h-3.5 w-3.5" /> {PLAY_STORE_URL ? t("available") : t("comingSoon")}
          </p>
          <h1 className="text-3xl sm:text-5xl font-bold">{t("title")}</h1>
          <p className="text-lg text-muted-foreground">{t("intro")}</p>
          {PLAY_STORE_URL ? (
            <Button asChild size="lg" className="gap-2"><a href={PLAY_STORE_URL}><PlayIcon className="h-5 w-5" /> {t("download")}</a></Button>
          ) : (
            <Button size="lg" className="gap-2" disabled><PlayIcon className="h-5 w-5" /> {t("downloadSoon")}</Button>
          )}
        </header>

        <section className="space-y-3 rounded-2xl border border-border p-5">
          <h2 className="flex items-center gap-2 text-xl font-semibold"><Smartphone className="h-5 w-5 text-primary" /> {t("nowTitle")}</h2>
          <p className="text-muted-foreground">{t("nowBody")}</p>
          {installed ? (
            <p className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground">
              <Check className="h-4 w-4" aria-hidden /> {t("installed")}
            </p>
          ) : prompt ? (
            <Button size="lg" className="gap-2" onClick={install}><Download className="h-5 w-5" /> {t("install")}</Button>
          ) : null}
          <ol className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><EllipsisVertical className="h-4 w-4 shrink-0" aria-hidden /> {t("now1")}</li>
            <li className="flex items-center gap-2"><Download className="h-4 w-4 shrink-0" aria-hidden /> {t("now2")}</li>
            <li className="flex items-center gap-2"><Smartphone className="h-4 w-4 shrink-0" aria-hidden /> {t("now3")}</li>
          </ol>
          <Button asChild variant="outline"><Link href="/home">{t("openBB")}</Link></Button>
        </section>

        <section className="space-y-3 rounded-2xl border border-border p-5">
          <h2 className="flex items-center gap-2 text-xl font-semibold"><FlaskConical className="h-5 w-5 text-primary" /> {t("foundersTitle")}</h2>
          <p className="text-muted-foreground">{t("foundersBody")}</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>{t("founders1")}</li>
            <li>{t("founders2")}</li>
            <li>{t("founders3")}</li>
          </ol>
        </section>

        <section className="space-y-3 rounded-2xl border border-border p-5">
          <h2 className="flex items-center gap-2 text-xl font-semibold"><Usb className="h-5 w-5 text-primary" /> {t("djTitle")}</h2>
          <p className="text-muted-foreground">{t("djBody")}</p>
          <Button asChild variant="outline"><Link href="/dj">{t("openDj")}</Link></Button>
        </section>
      </div>
    </div>
  );
}
